import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaSearch, FaPlus, FaList, FaTimes, FaCalendarAlt, FaEdit, FaTrash, FaEye, FaFilePdf } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { API_BASE_URL, getAuthHeaders } from '../../utils/apiConfig';
import { fetchMasterData } from '../../store/slices/masterDataSlice';
import { hasPermission } from '../../utils/permissions';
import {
  deleteSessionDocument,
  fetchSessionDocuments,
  updateSessionDocument,
  uploadSessionDocument
} from '../../utils/sessionsApi';
import { getSecureFileUrlWithToken } from '../../utils/secureFileAccess';
const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.7rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
`;

const IconButton = styled.button`
  background: transparent;
  border: 1px solid #e9ecef;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: #6c757d;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 15px 15px 0 0;
`;

const ModalTitle = styled.h2`
  color: #2c3e50;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #e9ecef;
    color: #2c3e50;
  }
`;

const ModalBody = styled.div`
  padding: 30px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 0.9rem;
  
  &:after {
    content: ${props => props.required ? '" *"' : '""'};
    color: #dc3545;
  }
`;

const Input = styled.input`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const Select = styled.select`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DateInput = styled(Input)`
  padding-right: 45px;
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 15px;
  color: #6c757d;
  pointer-events: none;
`;

const StatusSelectWrapper = styled.div`
  position: relative;
`;

const StatusSelect = styled(Select)`
  padding-right: 45px;
`;

const ClearStatusButton = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 2px;
  
  &:hover {
    color: #2c3e50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
`;

const SubmitButton = styled.button`
  background: rgb(51, 66, 147);;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  }
`;

const ResetButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: #5a6268;
    transform: translateY(-1px);
  }
`;

const SessionsContainer = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCheckbox = styled.input`
  margin-right: 10px;
  transform: scale(1.2);
`;

const HeaderText = styled.span`
  font-weight: 600;
  color: #2c3e50;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f8f9fa;
`;

const Th = styled.th`
  padding: 15px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
`;

const Td = styled.td`
  padding: 15px 20px;
  color: #495057;
  font-size: 0.95rem;
`;

const StatusText = styled.span`
  background: ${props => {
    const status = (props.status || '').toLowerCase();
    if (status === 'active') return '#28a745';
    if (status === 'not active') return '#dc3545';
    return '#6c757d';
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#dc3545' : 'rgb(51, 66, 147)'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 5px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SecondaryActionButton = styled.button`
  background: ${props => props.variant === 'master' ? '#6f42c1' : '#198754'};
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  margin: 0;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #2c3e50;
`;

const DocumentList = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  max-height: 260px;
  overflow: auto;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #f1f3f5;

  &:last-child {
    border-bottom: none;
  }
`;

const DocumentName = styled.div`
  flex: 1;
  font-size: 0.9rem;
  color: #2c3e50;
`;

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileInput = styled.input`
  font-size: 0.75rem;
  width: 170px;
`;

const EmptyText = styled.div`
  padding: 16px;
  color: #6c757d;
  font-size: 0.9rem;
`;

const DocCell = styled.td`
  min-width: 150px;
  text-align: center;
  vertical-align: middle;
`;

const ActionCell = styled.td`
  width: 120px;
  text-align: center;
  white-space: nowrap;
`;

const ScheduleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ScheduleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #495057;
  border-left: 3px solid rgb(51, 66, 147);
`;

const DayBadge = styled.span`
  background: rgb(51, 66, 147);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TimeRange = styled.span`
  color: #6c757d;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
`;

const NoSchedule = styled.span`
  color: #adb5bd;
  font-style: italic;
  font-size: 0.85rem;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 1.1rem;
`;

const ExampleText = styled.span`
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 4px;
  display: block;
`;

const TextArea = styled.textarea`
  padding: 12px 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const Sessions = () => {
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedSessionForDocuments, setSelectedSessionForDocuments] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState('syllabus');
  const [sessionDocuments, setSessionDocuments] = useState([]);
  const [newDocumentFile, setNewDocumentFile] = useState(null);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [newDocumentInputKey, setNewDocumentInputKey] = useState(0);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    courseId: '',
    sessionName: '',
    sessionDate: '',
    startTime: '',
    endTime: '',
    status: 'Active',
    days: []
  });

  const { confirmState, showDeleteConfirm } = useConfirm();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const canCreateSession = hasPermission(user, 'sessions_create');
  const canUpdateSession = hasPermission(user, 'sessions_update');
  const canDeleteSession = hasPermission(user, 'sessions_delete');
  const { courses: masterCourses, sessions: masterSessions, lastFetched, isLoading } = useSelector(state => state.masterData);


  useEffect(() => {
    // Only fetch if not already present
    if (!masterCourses.length || !masterSessions.length) {
      dispatch(fetchMasterData());
    }
  }, [dispatch, masterCourses.length, masterSessions.length]);

  const openModal = (session = null) => {
    if (session) {
      setEditingSession(session);
   
      
      // Format date for HTML date input (YYYY-MM-DD)
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        
        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        
        // Try to parse the date
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return '';
        }
        
        // Format as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      // Try different possible field names for the date
      const dateField = session.session_date || session.sessionDate || session.date || session.start_date;
      const formattedDate = formatDateForInput(dateField);
      
      setFormData({
        courseId: session.course_id || '',
        sessionName: session.session_name || '',
        sessionDate: formattedDate,
        startTime: session.start_time || '',
        endTime: session.end_time || '',
        status: session.status || 'Active',
        days: session.days || []
      });
      
      
  
    } else {
      setEditingSession(null);
      setFormData({
        courseId: '',
        sessionName: '',
        sessionDate: '',
        startTime: '',
        endTime: '',
        status: 'Active',
        days: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setFormData({
      courseId: '',
      sessionName: '',
      sessionDate: '',
      startTime: '',
      endTime: '',
      status: 'Scheduled',
      days: []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDayChange = (idx, field, value) => {
    setFormData(prev => {
      const newDays = [...prev.days];
      newDays[idx] = { ...newDays[idx], [field]: value };
      return { ...prev, days: newDays };
    });
  };

  const handleAddDay = () => {
    setFormData(prev => ({ ...prev, days: [...prev.days, { day: '', startTime: '', endTime: '' }] }));
  };

  const handleRemoveDay = (idx) => {
    setFormData(prev => {
      const newDays = prev.days.filter((_, i) => i !== idx);
      return { ...prev, days: newDays };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.courseId) {
      showError('Please select a course');
      return;
    }
    if (!formData.sessionDate) {
      showError('Please select a session date');
      return;
    }
    if (!formData.days || formData.days.length === 0 || formData.days.some(d => !d.day || !d.startTime || !d.endTime)) {
      showError('Please select at least one valid day and time');
      return;
    }
    try {
      const url = editingSession 
        ? `${API_BASE_URL}/sessions/${editingSession.id}`
        : `${API_BASE_URL}/sessions`;
      const method = editingSession ? 'PUT' : 'POST';
      const payload = { ...formData, days: formData.days };
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (response.ok) {
        showSuccess(result.message || (editingSession ? 'Session updated successfully' : 'Session created successfully'));
        dispatch(fetchMasterData());
        closeModal();
      } else {
        showError(result.message || 'Failed to save session');
      }
    } catch (error) {
      console.error('Error saving session:', error);
      showError('Error saving session');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirm(
      masterSessions.find(session => session.id === id)?.session_name || 'Session',
      'session'
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() },
      });

      const result = await response.json();

      if (response.ok) {
        showSuccess(result.message || 'Session deleted successfully');
        dispatch(fetchMasterData());
      } else {
        showError(result.message || 'Failed to delete session');
      }
    } catch (error) {
      showError('Error deleting session');
    }
  };

  const handleReset = () => {
    setFormData({
      courseId: '',
      sessionName: '',
      sessionDate: '',
      startTime: '',
      endTime: '',
      status: 'Active',
      days: []
    });
  };

  const clearStatus = () => {
    setFormData(prev => ({
      ...prev,
      status: ''
    }));
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedSessions(masterSessions.map(session => session.id));
    } else {
      setSelectedSessions([]);
    }
  };

  const handleSelectSession = (sessionId) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const formatSchedule = (days) => {
    if (!Array.isArray(days) || days.length === 0) {
      return <NoSchedule>No schedule set</NoSchedule>;
    }

    const validDays = days.filter(d => d.day && d.startTime && d.endTime);
    
    if (validDays.length === 0) {
      return <NoSchedule>Incomplete schedule</NoSchedule>;
    }

    return (
      <ScheduleContainer>
        {validDays.map((day, index) => (
          <ScheduleItem key={index}>
            <DayBadge>{day.day.slice(0, 3)}</DayBadge>
            <TimeRange>
              {day.startTime} - {day.endTime}
            </TimeRange>
          </ScheduleItem>
        ))}
      </ScheduleContainer>
    );
  };

  const normalizeFilePath = (filePath) => {
    if (!filePath) return '';
    if (filePath.startsWith('admin/backend/uploads/')) {
      return filePath.replace('admin/backend/uploads/', '');
    }
    if (filePath.includes('uploads/')) {
      const uploadsIndex = filePath.indexOf('uploads/');
      return filePath.substring(uploadsIndex + 'uploads/'.length);
    }
    if (filePath.startsWith('admin/backend/')) {
      return filePath.replace('admin/backend/', '');
    }
    return filePath;
  };

  const loadSessionDocuments = async (sessionId, documentType) => {
    setIsDocumentsLoading(true);
    try {
      const response = await fetchSessionDocuments(sessionId);
      if (!response.success) {
        showError(response.message || 'Failed to load documents');
        setSessionDocuments([]);
        return;
      }
      const filtered = (response.documents || []).filter(doc => doc.document_type === documentType);
      setSessionDocuments(filtered);
    } catch (error) {
      showError('Error loading documents');
      setSessionDocuments([]);
    } finally {
      setIsDocumentsLoading(false);
    }
  };

  const openDocumentModal = async (session, documentType) => {
    setSelectedSessionForDocuments(session);
    setSelectedDocumentType(documentType);
    setNewDocumentFile(null);
    setShowDocumentModal(true);
    await loadSessionDocuments(session.id, documentType);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedSessionForDocuments(null);
    setSelectedDocumentType('syllabus');
    setSessionDocuments([]);
    setNewDocumentFile(null);
    setEditingDocumentId(null);
    setNewDocumentInputKey(prev => prev + 1);
  };

  const handleUploadNewDocument = async () => {
    if (!selectedSessionForDocuments) return;
    if (!newDocumentFile) {
      showError('Please choose a PDF file to upload');
      return;
    }
    try {
      const response = await uploadSessionDocument(
        selectedSessionForDocuments.id,
        selectedDocumentType,
        newDocumentFile
      );
      if (response.success) {
        showSuccess('Document uploaded successfully');
        setNewDocumentFile(null);
        setNewDocumentInputKey(prev => prev + 1);
        await loadSessionDocuments(selectedSessionForDocuments.id, selectedDocumentType);
        dispatch(fetchMasterData());
      } else {
        showError(response.message || 'Failed to upload document');
      }
    } catch (error) {
      showError('Error uploading document');
    }
  };

  const handleReplaceDocument = async (documentId, file) => {
    if (!selectedSessionForDocuments || !file) return;
    try {
      const response = await updateSessionDocument(selectedSessionForDocuments.id, documentId, file);
      if (response.success) {
        showSuccess('Document updated successfully');
        await loadSessionDocuments(selectedSessionForDocuments.id, selectedDocumentType);
        setEditingDocumentId(null);
      } else {
        showError(response.message || 'Failed to update document');
      }
    } catch (error) {
      showError('Error updating document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!selectedSessionForDocuments) return;
    const confirmed = await showDeleteConfirm('this document', selectedDocumentType === 'syllabus' ? 'syllabus file' : 'master data file');
    if (!confirmed) return;

    try {
      const response = await deleteSessionDocument(selectedSessionForDocuments.id, documentId);
      if (response.success) {
        showSuccess('Document deleted successfully');
        await loadSessionDocuments(selectedSessionForDocuments.id, selectedDocumentType);
        dispatch(fetchMasterData());
      } else {
        showError(response.message || 'Failed to delete document');
      }
    } catch (error) {
      showError('Error deleting document');
    }
  };

  const handleViewDocument = (filePath) => {
    const token = localStorage.getItem('token');
    if (!token || !filePath) {
      showError('Unable to open file');
      return;
    }
    const normalizedPath = normalizeFilePath(filePath);
    const secureUrl = getSecureFileUrlWithToken(normalizedPath, token);
    window.open(secureUrl, '_blank');
  };

  if (isLoading && masterSessions.length === 0) {
    return (
      <Container>
        <LoadingText>Loading sessions...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Sessions</Title>
        <ActionButtons>
          {/* <IconButton>
            <FaSearch />
          </IconButton> */}
          {canCreateSession && (
            <AddButton onClick={() => openModal()}>
              <FaPlus /> Add New Session
            </AddButton>
          )}
          {/* <IconButton>
            <FaList />
          </IconButton> */}
        </ActionButtons>
      </Header>

      <SessionsContainer>
        

        <Table style={{ display: 'inline-table' }}>
          <Thead>
            <Tr>
              <Th style={{ width: '50px' }}></Th>
              <Th>Session Name</Th>
              <Th>Course</Th>
              <Th>Start Date</Th>
              <Th>Status</Th>
              <Th>Schedule</Th>
              <Th style={{ minWidth: '150px', textAlign: 'center' }}>Syllabus</Th>
              <Th style={{ minWidth: '170px', textAlign: 'center' }}>Master Schedule</Th>
              <Th style={{ width: '120px', textAlign: 'center' }}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {masterSessions.map((session) => (
              <Tr key={session.id}>
                <Td>
                  {/* <TableCheckbox
                    type="checkbox"
                    checked={selectedSessions.includes(session.id)}
                    onChange={() => handleSelectSession(session.id)}
                  /> */}
                </Td>
                <Td>{session.session_name || '-'}</Td>
                <Td>{session.course_name}</Td>
                <Td>{session.session_date ? new Date(session.session_date).toLocaleDateString() : '-'}</Td>
                
                <Td>
                  <StatusText status={session.status}>
                    {session.status}
                  </StatusText>
                </Td>
                <Td>{formatSchedule(session.days)}</Td>
                <DocCell>
                  {canUpdateSession ? (
                    <SecondaryActionButton
                      onClick={() => openDocumentModal(session, 'syllabus')}
                      title="Syllabus PDFs"
                    >
                      <FaFilePdf /> Syllabus ({session.syllabus_count || 0})
                    </SecondaryActionButton>
                  ) : (
                    '—'
                  )}
                </DocCell>
                <DocCell>
                  {canUpdateSession ? (
                    <SecondaryActionButton
                      variant="master"
                      onClick={() => openDocumentModal(session, 'master_data')}
                      title="Master schedule PDFs"
                    >
                      <FaFilePdf /> Master Schedule ({session.master_data_count || 0})
                    </SecondaryActionButton>
                  ) : (
                    '—'
                  )}
                </DocCell>
                <ActionCell>
                  {canUpdateSession && (
                    <ActionButton onClick={() => openModal(session)}>
                      <FaEdit />
                    </ActionButton>
                  )}
                  {canDeleteSession && (
                    <ActionButton danger onClick={() => handleDelete(session.id)}>
                      <FaTrash />
                    </ActionButton>
                  )}
                </ActionCell>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </SessionsContainer>

      {/* Custom Confirmation Dialog */}
      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        details={confirmState.details}
        itemName={confirmState.itemName}
      />

      {showDocumentModal && (
        <ModalOverlay onClick={closeDocumentModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {selectedDocumentType === 'syllabus' ? 'Syllabus Files' : 'Master Schedule'}
                {selectedSessionForDocuments
                  ? ` — ${selectedSessionForDocuments.session_name || selectedSessionForDocuments.course_name || 'Session'}`
                  : ''}
              </ModalTitle>
              <CloseButton onClick={closeDocumentModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <SectionTitle>Add New PDF</SectionTitle>
              <FormGroup>
                <FileInput
                  key={newDocumentInputKey}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setNewDocumentFile(e.target.files?.[0] || null)}
                />
              </FormGroup>
              <ButtonGroup>
                <ResetButton
                  type="button"
                  onClick={() => {
                    setNewDocumentFile(null);
                    setNewDocumentInputKey(prev => prev + 1);
                  }}
                >
                  Clear
                </ResetButton>
                <SubmitButton type="button" onClick={handleUploadNewDocument}>
                  Upload
                </SubmitButton>
              </ButtonGroup>

              <SectionTitle style={{ marginTop: '20px' }}>Uploaded Files</SectionTitle>
              <DocumentList>
                {isDocumentsLoading ? (
                  <EmptyText>Loading documents...</EmptyText>
                ) : sessionDocuments.length === 0 ? (
                  <EmptyText>No files uploaded yet.</EmptyText>
                ) : (
                  sessionDocuments.map((doc) => (
                    <DocumentItem key={doc.id}>
                      <DocumentName>{doc.file_name}</DocumentName>
                      <DocumentActions>
                        <ActionButton type="button" onClick={() => handleViewDocument(doc.file_path)}>
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          type="button"
                          onClick={() => setEditingDocumentId(prev => (prev === doc.id ? null : doc.id))}
                        >
                          {editingDocumentId === doc.id ? <FaTimes /> : <FaEdit />}
                        </ActionButton>
                        {editingDocumentId === doc.id && (
                          <FileInput
                            type="file"
                            accept=".pdf,application/pdf"
                            onChange={(e) => handleReplaceDocument(doc.id, e.target.files?.[0] || null)}
                          />
                        )}
                        <ActionButton danger type="button" onClick={() => handleDeleteDocument(doc.id)}>
                          <FaTrash />
                        </ActionButton>
                      </DocumentActions>
                    </DocumentItem>
                  ))
                )}
              </DocumentList>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingSession ? 'Edit Session' : 'Add Session'}</ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label required>Course</Label>
                  <Select
                    name="courseId"
                    value={formData.courseId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Course</option>
                    {masterCourses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.course_name} ({course.course_code})
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Session Name</Label>
                  <Input
                    type="text"
                    name="sessionName"
                    value={formData.sessionName}
                    onChange={handleInputChange}
                    placeholder="e.g., Introduction to Patient Care"
                  />
                  <ExampleText>Example: Introduction to Patient Care, Lab Session 1</ExampleText>
                </FormGroup>

                <FormGroup>
                  <Label required>Session Start Date</Label>
                  <DateInputWrapper>
                                      <DateInput
                    type="date"
                    name="sessionDate"
                    value={formData.sessionDate || ''}
                    onChange={handleInputChange}
                    required
                  />
                   
                  </DateInputWrapper>
                </FormGroup>

               

                <FormGroup>
                  <Label>Status</Label>
                  <StatusSelectWrapper>
                    <StatusSelect
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Not Active">Not Active</option>
                    </StatusSelect>
                    {formData.status && (
                      <ClearStatusButton onClick={clearStatus}>
                        <FaTimes />
                      </ClearStatusButton>
                    )}
                  </StatusSelectWrapper>
                </FormGroup>

                <FormGroup>
                  <Label>Days</Label>
                  {formData.days.map((entry, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                      <Select
                        value={entry.day}
                        onChange={e => handleDayChange(idx, 'day', e.target.value)}
                        style={{ minWidth: 120 }}
                      >
                        <option value="">Select Day</option>
                        {DAYS_OF_WEEK.filter(d => !formData.days.some((e, i) => e.day === d && i !== idx)).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </Select>
                      <Input
                        type="time"
                        value={entry.startTime}
                        onChange={e => handleDayChange(idx, 'startTime', e.target.value)}
                        placeholder="Start Time"
                        style={{ minWidth: 100 }}
                      />
                      <Input
                        type="time"
                        value={entry.endTime}
                        onChange={e => handleDayChange(idx, 'endTime', e.target.value)}
                        placeholder="End Time"
                        style={{ minWidth: 100 }}
                      />
                      {formData.days.length > 1 && (
                        <button type="button" onClick={() => handleRemoveDay(idx)} style={{ color: 'red', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>
                      )}
                    </div>
                  ))}
                  {formData.days.length < 7 && (
                    <button type="button" onClick={handleAddDay} style={{ marginTop: 4, color: '#334293', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>＋</button>
                  )}
                </FormGroup>

                <ButtonGroup>
                  <ResetButton type="button" onClick={handleReset}>
                    Reset
                  </ResetButton>
                  <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : (editingSession ? 'Update' : 'Add')}
                  </SubmitButton>
                </ButtonGroup>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Sessions; 